import { Injectable, signal, computed } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Observable } from 'rxjs'
import { ServerUrlService } from './server-url.service'
import { AuthService } from './auth.service'
import { ResBodyType } from 'otimus-shared'
import { MenuSearchResponse } from '../components/sidebar/entities/menu-search-response.entity'
import { NavigationMenuResponse } from '../components/sidebar/entities/navigation-menu-response.entity'
import { FlatNavigationItem } from '../components/sidebar/entities/flat-navigation-item.entity'
import { NavigationMenuProgram } from '../components/sidebar/entities/navigation-menu-item.entity'

const STORAGE_KEY = 'sidebar-collapsed'

@Injectable({ providedIn: 'root' })
export class SidebarService {
  readonly collapsed = signal<boolean>(this.loadCollapsed())
  readonly mobileOpen = signal<boolean>(false)
  readonly manuallyOpened = signal<boolean>(!this.loadCollapsed())
  readonly width = computed(() => (this.collapsed() ? '93px' : '240px'))
  readonly expandedIds = signal<Set<number>>(new Set())
  readonly flatMenuItems = signal<FlatNavigationItem[]>([])
  readonly favoritePrograms = signal<NavigationMenuProgram[]>([])

  constructor(
    private http: HttpClient,
    private serverUrl: ServerUrlService,
    private authService: AuthService
  ) {}

  toggle(): void {
    if (this.manuallyOpened()) {
      this.manuallyOpened.set(false)
      this.collapsed.set(true)
      this.collapseAll()
    } else {
      this.manuallyOpened.set(true)
      this.collapsed.set(false)
    }
    localStorage.setItem(STORAGE_KEY, String(this.collapsed()))
  }

  expandByHover(): void {
    this.collapsed.set(false)
    this.manuallyOpened.set(false)
  }

  collapseByHover(): void {
    if (this.manuallyOpened()) {
      return
    }
    this.collapsed.set(true)
    this.collapseAll()
  }

  toggleExpanded(id: number, siblingIds?: number[]): void {
    this.expandedIds.update((set) => {
      const next = new Set(set)
      if (next.has(id)) {
        next.delete(id)
        return next
      }
      if (siblingIds) {
        for (const sid of siblingIds) {
          if (sid !== id) {
            next.delete(sid)
          }
        }
      }
      next.add(id)
      return next
    })
  }

  isExpanded(id: number): boolean {
    return this.expandedIds().has(id)
  }

  collapseAll(): void {
    this.expandedIds.set(new Set())
  }

  expandIds(ids: Iterable<number>): void {
    this.expandedIds.update((set) => {
      const next = new Set(set)
      for (const id of ids) {
        next.add(id)
      }
      return next
    })
  }

  openMobile(): void {
    this.mobileOpen.set(true)
  }

  closeMobile(): void {
    this.mobileOpen.set(false)
  }

  findMenuItems(): Observable<ResBodyType<NavigationMenuResponse>> {
    return this.http.get<ResBodyType<NavigationMenuResponse>>(
      `${this.serverUrl.url}/system/navigation-menu`,
      { headers: { Authorization: `Bearer ${this.authService.token}` } }
    )
  }

  addFavorite(programId: number): Observable<ResBodyType<void>> {
    return this.http.post<ResBodyType<void>>(
      `${this.serverUrl.url}/system/navigation-menu/favorites/${programId}`,
      null,
      { headers: { Authorization: `Bearer ${this.authService.token}` } }
    )
  }

  deleteFavorite(programId: number): Observable<ResBodyType<void>> {
    return this.http.delete<ResBodyType<void>>(
      `${this.serverUrl.url}/system/navigation-menu/favorites/${programId}`,
      { headers: { Authorization: `Bearer ${this.authService.token}` } }
    )
  }

  searchMenu(query: string): Observable<ResBodyType<MenuSearchResponse>> {
    const params = new HttpParams().set('q', query)
    return this.http.get<ResBodyType<MenuSearchResponse>>(
      `${this.serverUrl.url}/system/menu-search`,
      {
        params,
        headers: { Authorization: `Bearer ${this.authService.token}` },
      }
    )
  }

  private loadCollapsed(): boolean {
    return localStorage.getItem(STORAGE_KEY) === 'true'
  }
}
